/**
 * React Query hooks for Permissions management
 * 
 * Handles fetching and mutating permissions for:
 * - Entity-level (agents, collections, mcp_servers)
 * - Roles (list, create, member management)
 * - Modules (list, permission CRUD)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import type { 
  PermissionAssignment, 
  PermissionSubject, 
  GrantPermissionInput,
  RevokePermissionInput,
  DenyPermissionInput,
  Role,
  RoleMember,
  RoleCreateInput,
  ModulePermission,
  EffectivePermission,
  ModulePermissionGrantInput,
} from '../types';
import { useGroups, type Group } from '@/hooks/use-groups';
import { useTenant } from '@/contexts/TenantContext';

/**
 * Helper hook to get the current tenant ID.
 * Returns undefined if no tenant is selected.
 */
function useTenantId(): string | undefined {
  const { currentTenant } = useTenant();
  return currentTenant?.id;
}

/**
 * Maps entity type to the correct API path segment.
 */
function entityBasePath(entityType: string, entityId: string, tenantId: string): string {
  const pathMap: Record<string, string> = {
    agent: `tenants/${tenantId}/agents/${entityId}/permissions`,
    collection: `tenants/${tenantId}/knowledge/collections/${entityId}/permissions`,
    mcp_server: `tenants/${tenantId}/mcp/${entityId}/permissions`,
  };
  return pathMap[entityType] || `tenants/${tenantId}/${entityType}s/${entityId}/permissions`;
}

// =============================================================================
// Entity Permissions Hooks
// =============================================================================

/**
 * Fetch effective permissions for an entity (agent, collection, or mcp_server)
 */
export function useEffectivePermissions(entityType: string, entityId: string) {
  const tenantId = useTenantId();
  
  return useQuery({
    queryKey: ['permissions', 'effective', entityType, entityId, tenantId],
    queryFn: async () => {
      const basePath = entityBasePath(entityType, entityId, tenantId ?? "");
      const { data } = await apiClient.get<{ permissions: EffectivePermission[]; total: number }>(
        `/api/v1/${basePath}/effective`,
      );
      return data;
    },
    enabled: !!entityId && !!tenantId,
  });
}

/**
 * Check if current user can manage permissions for an entity
 */
export function useCanManagePermissions(entityType: string, entityId: string) {
  const tenantId = useTenantId();
  
  return useQuery({
    queryKey: ['permissions', 'can-manage', entityType, entityId, tenantId],
    queryFn: async () => {
      const basePath = entityBasePath(entityType, entityId, tenantId ?? "");
      const { data } = await apiClient.get<{ can_manage: boolean }>(`/api/v1/${basePath}/can-manage`);
      return data;
    },
    enabled: !!entityId && !!tenantId,
  });
}

/**
 * Fetch permissions for an entity — uses effective permissions endpoint
 */
export function usePermissions(entityType: string, entityId: string) {
  const tenantId = useTenantId();
  
  return useQuery({
    queryKey: ['permissions', entityType, entityId, tenantId],
    queryFn: async () => {
      const basePath = entityBasePath(entityType, entityId, tenantId ?? "");
      const response = await apiClient.get<EffectivePermission[]>(
        `/api/v1/${basePath}/effective`,
      );
      // The effective endpoint returns a BARE ARRAY (not a wrapped object).
      const items = Array.isArray(response.data)
        ? response.data
        : ((response.data as unknown as { permissions: EffectivePermission[] }).permissions || []);
      // Map effective permissions to PermissionAssignment for backward compat
      const assignments: PermissionAssignment[] = items.map(
        (p: EffectivePermission) => ({
          subjectId: p.subject_id,
          subjectType: p.subject_type as 'user' | 'group',
          subjectName: p.subject_name || p.subject_id,
          subjectEmail: p.subject_email,
          permission: p.effective_level as PermissionAssignment['permission'],
          source: p.source,
          isDenied: p.is_denied,
          grantedAt: '',
          grantedBy: '',
        })
      );
      return assignments;
    },
    enabled: !!entityId && !!tenantId,
  });
}

/**
 * Fetch available subjects (users and groups) that can be assigned permissions
 * Uses the Groups API to get groups as subjects
 */
export function useAvailableSubjects() {
  const { data: groupsData, isLoading: loadingGroups, error: groupsError } = useGroups();
  const { data: membersData, isLoading: loadingMembers, error: membersError } = useTenantMembers();

  // Groups as subjects
  const groupSubjects: PermissionSubject[] = (groupsData?.items || []).map((group: Group) => ({
    id: group.id,
    type: 'group' as const,
    name: group.name,
    memberCount: group.member_count,
  }));

  // Users as subjects (tenant members with contact data — ISS-315)
  const userSubjects: PermissionSubject[] = (membersData?.items || []).map((member: TenantMember) => ({
    id: member.user_id,
    type: 'user' as const,
    name: member.email || member.name || 'Email no disponible',
    email: member.email,
  }));

  return {
    data: [...groupSubjects, ...userSubjects],
    isLoading: loadingGroups || loadingMembers,
    error: groupsError ?? membersError,
  };
}

/**
 * Grant permission to a subject for an entity
 */
/**
 * Map frontend permission levels to Keto relation names.
 * Keto OPL: admin ← owners, edit ← editors, use/view ← users, operate ← operators
 */
function mapPermissionToRelation(permission: string): string {
  const mapping: Record<string, string> = {
    admin: 'owners',
    edit: 'editors',
    operate: 'operators',
    use: 'users',
  };
  return mapping[permission] ?? 'users';
}

export function useGrantPermission() {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: GrantPermissionInput) => {
      const basePath = entityBasePath(data.entityType, data.entityId, tenantId ?? "");
      await apiClient.post(`/api/v1/${basePath}`, {
        subject_id: data.subjectId,
        subject_type: data.subjectType === 'group' ? 'Group' : 'User',
        relation: mapPermissionToRelation(data.permission),
      });
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ 
        queryKey: ['permissions', vars.entityType, vars.entityId] 
      });
      queryClient.invalidateQueries({
        queryKey: ['permissions', 'effective', vars.entityType, vars.entityId]
      });
    },
  });
}

/**
 * Revoke permission from a subject for an entity
 */
export function useRevokePermission() {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: RevokePermissionInput) => {
      const basePath = entityBasePath(data.entityType, data.entityId, tenantId ?? "");
      await apiClient.delete(`/api/v1/${basePath}/${data.subjectId}`);
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ 
        queryKey: ['permissions', vars.entityType, vars.entityId] 
      });
      queryClient.invalidateQueries({
        queryKey: ['permissions', 'effective', vars.entityType, vars.entityId]
      });
    },
  });
}

/**
 * Add explicit deny for a subject on an entity
 */
export function useDenyPermission() {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: DenyPermissionInput) => {
      const basePath = entityBasePath(data.entityType, data.entityId, tenantId ?? "");
      await apiClient.post(`/api/v1/${basePath}/deny`, {
        subject_id: data.subjectId,
        subject_type: data.subjectType,
        level: data.level,
      });
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ 
        queryKey: ['permissions', vars.entityType, vars.entityId] 
      });
      queryClient.invalidateQueries({
        queryKey: ['permissions', 'effective', vars.entityType, vars.entityId]
      });
    },
  });
}

// =============================================================================
// Roles Hooks
// =============================================================================

interface RolesListResponse {
  items: Role[];
  total: number;
}

/**
 * Fetch all roles for the current tenant
 */
export function useRoles() {
  const tenantId = useTenantId();
  
  return useQuery({
    queryKey: ['roles', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get<RolesListResponse>('/api/v1/roles');
      return data;
    },
    enabled: !!tenantId,
  });
}

interface RoleMembersResponse {
  items: RoleMember[];
  total: number;
}

/**
 * Fetch members of a specific role
 */
export function useRoleMembers(roleId: string, options?: { enabled?: boolean }) {
  const tenantId = useTenantId();
  
  return useQuery({
    queryKey: ['role-members', roleId, tenantId],
    queryFn: async () => {
      // Role ID format: "rolename@tenant_id" - extract just the name
      const roleName = roleId.split('@')[0];
      const { data } = await apiClient.get<RoleMembersResponse>(`/api/v1/roles/${roleName}/members`);
      return data.items;
    },
    enabled: !!tenantId && !!roleId && (options?.enabled !== false),
  });
}

/**
 * Create a new custom role
 */
export function useCreateRole() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (roleData: RoleCreateInput) => {
      await apiClient.post('/api/v1/roles', {
        name: roleData.name,
        description: roleData.description,
        module_permissions: roleData.module_permissions,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });
}

/**
 * Delete a custom role
 */
export function useDeleteRole() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (roleId: string) => {
      const roleName = roleId.split('@')[0];
      await apiClient.delete(`/api/v1/roles/${roleName}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });
}

/**
 * Add a member to a role
 */
export function useAddRoleMember() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ roleId, userId }: { roleId: string; userId: string }) => {
      const roleName = roleId.split('@')[0];
      await apiClient.post(`/api/v1/roles/${roleName}/members`, {
        user_id: userId,
      });
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['role-members', vars.roleId] });
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });
}

/**
 * Remove a member from a role
 */
export function useRemoveRoleMember() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ roleId, userId }: { roleId: string; userId: string }) => {
      const roleName = roleId.split('@')[0];
      await apiClient.delete(`/api/v1/roles/${roleName}/members/${userId}`);
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['role-members', vars.roleId] });
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });
}

// =============================================================================
// Modules Hooks
// =============================================================================

interface ModulesListResponse {
  items: { id: string; name: string; description?: string }[];
  total: number;
}

/**
 * Fetch all modules for the current tenant
 */
export function useModules() {
  const tenantId = useTenantId();
  
  return useQuery({
    queryKey: ['modules', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get<ModulesListResponse>('/api/v1/modules');
      return data;
    },
    enabled: !!tenantId,
  });
}

interface ModulePermissionsResponse {
  module: string;
  tenant_id: string;
  permissions: ModulePermission[];
  total: number;
}

/**
 * Fetch permissions for a specific module
 */
export function useModulePermissions(module: string) {
  const tenantId = useTenantId();
  
  return useQuery({
    queryKey: ['module-permissions', module, tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get<ModulePermissionsResponse>(`/api/v1/modules/${module}/permissions`);
      return data;
    },
    enabled: !!tenantId && !!module,
  });
}

/**
 * Grant a permission on a module
 */
export function useGrantModulePermission() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (grantData: ModulePermissionGrantInput) => {
      await apiClient.post(`/api/v1/modules/${grantData.module}/permissions`, {
        subject_id: grantData.subject_id,
        subject_type: grantData.subject_type,
        level: grantData.level,
      });
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['module-permissions', vars.module] });
    },
  });
}

/**
 * Revoke a permission from a module
 */
export function useRevokeModulePermission() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ module, subjectId }: { module: string; subjectId: string }) => {
      await apiClient.delete(`/api/v1/modules/${module}/permissions/${subjectId}`);
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['module-permissions', vars.module] });
    },
  });
}

// =============================================================================
// Tenant Members Hook
// =============================================================================

export interface TenantMember {
  user_id: string;
  name?: string;
  email?: string;
  role: string;
  status?: string;
  joined_at?: string;
}

interface TenantMembersResponse {
  items: TenantMember[];
  total: number;
}

/**
 * Fetch all members of the current tenant
 * Note: members endpoint is under /api/v1/admin/tenants/{tenant_id}/members
 */
export function useTenantMembers() {
  const tenantId = useTenantId();
  
  return useQuery({
    queryKey: ['tenant-members', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get<TenantMembersResponse>(`/api/v1/admin/tenants/${tenantId}/members`);
      return data;
    },
    enabled: !!tenantId,
  });
}

export interface IdentityLookup {
  id: string;
  email: string | null;
  name: string | null;
}

/**
 * Add a new member to the current tenant.
 * Requires tenant admin permission.
 */
export function useAddTenantMember() {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (memberData: { user_id: string; role: string }) => {
      const { data } = await apiClient.post<TenantMember>(`/api/v1/admin/tenants/${tenantId}/members`, memberData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-members', tenantId] });
    },
  });
}

/**
 * Remove a member from the current tenant (Sprint 209 follow-up).
 * Backend: DELETE /api/v1/tenants/{tenant_id}/members/{user_id}
 * (TenantAdmin) — removes the projection row + Keto DR relations.
 */
export function useRemoveTenantMember() {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      await apiClient.delete(`/api/v1/admin/tenants/${tenantId}/members/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-members', tenantId] });
    },
  });
}

/**
 * Look up a Kratos identity by email before adding them to the tenant.
 */
export function useLookupIdentityByEmail() {
  return useMutation({
    // ISS-463: tenantId se pasa en el submit (nunca del closure del primer
    // render; useMutation congela el closure y el cambio de tenant activo
    // dejaba el lookup apuntando al tenant anterior (System)).
    mutationFn: async (args: { email: string; tenantId: string }) => {
      const { email, tenantId } = args;
      const { data } = await apiClient.get<IdentityLookup>(
        `/api/v1/admin/tenants/${tenantId}/members/lookup?email=${encodeURIComponent(email)}`
      );
      return data;
    },
  });
}

// =============================================================================
// Tenant Access (ISS-474)
// =============================================================================

export interface TenantAccessInfo {
  tenant_id: string;
  role: 'owner' | 'admin' | 'member';
  is_tenant_admin: boolean;
}

/**
 * Resolve the current user's role on the ACTIVE tenant from Keto (SSoT)
 * via /auth/me/tenant-access. Fail-closed: on error or missing membership
 * the hook reports no admin access, so privileged UI entries stay hidden.
 */
export function useTenantAccess() {
  const tenantId = useTenantId();

  return useQuery({
    queryKey: ['tenant-access', tenantId],
    queryFn: async (): Promise<TenantAccessInfo | null> => {
      if (!tenantId) return null;
      const { data } = await apiClient.get<TenantAccessInfo>(
        `/api/v1/auth/me/tenant-access?tenant_id=${encodeURIComponent(tenantId)}`
      );
      return data;
    },
    retry: false,
    enabled: Boolean(tenantId),
  });
}
